import React, { useEffect, useRef, useState } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import { useAuth } from '../../contexts/AuthContext';
import signupImage from '../../assets/images/services-2.png';
import tecnvironsLogo from '../../assets/images/Tecnvirons-logo.jpeg';
import 'react-phone-input-2/lib/style.css';
import '../common/phone-input-overrides.css';
import './SignupModal.css'; // Reusing the same CSS

const AdminSignupModal = ({ isOpen, onClose, onSwitchToLogin, onAdminEmailVerification }) => {
  const [phoneAuthMethod, setPhoneAuthMethod] = useState(null);
  const [showPhoneMethodSelection, setShowPhoneMethodSelection] = useState(false);
  const [isIndianPhone, setIsIndianPhone] = useState(false);

  const { adminSignup } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const formContentRef = useRef(null);

  const scrollToFirstError = () => {
    window.requestAnimationFrame(() => {
      const formContent = formContentRef.current;
      if (!formContent) return;

      const firstError = formContent.querySelector('.error-message, .field-error');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      } else {
        formContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  useEffect(() => {
    if (!Object.keys(errors).length) return;
    scrollToFirstError();
  }, [errors]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toE164 = (value) => (value ? `+${value}` : '');

  const isValidInternationalPhone = (value) => {
    if (!value) return false;
    const parsed = parsePhoneNumberFromString(toE164(value));
    return !!parsed && parsed.isValid();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'fullName') {
      if (value !== '' && !/^[A-Za-z\s]+$/.test(value)) {
        setErrors(prev => ({ ...prev, fullName: 'Full name must contain only alphabets and spaces' }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidInternationalPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (isIndianPhone && !phoneAuthMethod) {
      newErrors.phoneMethod = 'Please select SMS or WhatsApp';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the Terms of Service and Privacy Policy';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError();
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const signupData = {
        authType: 'both',
        identifier: formData.email,
        email: formData.email,
        phone: formData.phone ? toE164(formData.phone) : '',
        mobile_number: formData.phone ? toE164(formData.phone) : '',
        phoneAuthMethod: phoneAuthMethod,
        countryCode: null,
        password: formData.password,
        fullName: formData.fullName,
      };

      const result = await adminSignup(signupData);

      if (result.success) {
        setErrors({});
        if (onAdminEmailVerification) {
          onAdminEmailVerification({
            identifier: formData.email,
            email: formData.email,
            phone: formData.phone ? `+${formData.phone.replace(/\D/g, '')}` : '',
            authType: 'both',
            method: phoneAuthMethod
          });
        }
      } else {
        setErrors({ general: result.error || 'Admin signup failed. Please try again.' });
        scrollToFirstError();
      }
    } catch (error) {
      setErrors({ general: error?.message || 'An unexpected error occurred. Please try again.' });
      scrollToFirstError();
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="signup-modal-overlay" onClick={onClose}>
      <div
        className="signup-modal-container auth-modal"
        onClick={e => e.stopPropagation()}
        style={{ '--modal-bg-image': `url(${signupImage})` }}
      >
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
        <div className="modal-form-section">
          <div className="form-content" ref={formContentRef}>
            <div className="auth-modal-header text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', marginTop: '2px' }}>
              <div className="modal-logo" style={{ marginBottom: '20px', width: '100%', display: 'flex', justifyContent: 'center' }}>
                <img
                  src={tecnvironsLogo}
                  alt="Tecnvirons"
                  className="modal-logo-image"
                  style={{ width: '60px', maxWidth: '100%', height: 'auto', objectFit: 'contain', backgroundColor: 'transparent', display: 'block', margin: '0 auto' }}
                />
              </div>
            </div>

            <div className="auth-toggle">
              <button className="toggle-btn active">HQ Sign Up</button>
              <button className="toggle-btn" onClick={onSwitchToLogin}>HQ Log In</button>
            </div>

            <div className="form-main">
              <h2 className="form-title text-center">Create HQ Account</h2>
              <p className="form-subtitle text-center">Join the Headquarters team today</p>

              <div className="modal-form-group">
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  required
                />
                <label htmlFor="fullName">Full Name</label>
                {errors.fullName && <span className="field-error">{errors.fullName}</span>}
              </div>

              {errors.general && (
                <div className="error-message">
                  {errors.general}
                </div>
              )}

              <form onSubmit={handleSubmit} className="signup-form">
                <div className="modal-form-group">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  />
                  <label htmlFor="email">Email</label>
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>

                <div className="modal-form-group modal-form-group-phone">
                  <label htmlFor="phone" style={{ display: 'block', marginBottom: '8px', color: '#4b5563', fontSize: '14px' }}>Phone Number</label>
                  <PhoneInput
                    value={formData.phone}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, phone: value }));
                      if (errors.phone) {
                        setErrors(prev => ({ ...prev, phone: '' }));
                      }

                      if (errors.phoneMethod) {
                        setErrors(prev => ({ ...prev, phoneMethod: '' }));
                      }

                      const digits = (value || '').replace(/\D/g, '');
                      const hasEnoughDigits = digits.length >= 7;
                      const currentIsIndian = digits.startsWith('91');

                      setIsIndianPhone(currentIsIndian);

                      if (!hasEnoughDigits) {
                        setShowPhoneMethodSelection(false);
                        setPhoneAuthMethod(null);
                      } else if (currentIsIndian) {
                        setShowPhoneMethodSelection(true);
                        setPhoneAuthMethod(null);
                      } else {
                        setShowPhoneMethodSelection(false);
                        setPhoneAuthMethod('whatsapp');
                      }
                    }}
                    country="in"
                    countryCodeEditable={true}
                    disableDropdown={false}
                    enableSearch={true}
                    preferredCountries={['in', 'us', 'gb', 'ae', 'sa', 'pk', 'bd']}
                    separateDialCode={true}
                    searchPlaceholder="Search country..."
                    searchNotFound="No country found"
                    disabled={isLoading}
                  />
                  {errors.phone && <span className="field-error">{errors.phone}</span>}
                </div>

                {showPhoneMethodSelection && (
                  <div style={{
                    padding: '18px 16px',
                    background: 'linear-gradient(135deg, #f0f4ff 0%, #f8ebff 100%)',
                    borderRadius: '8px',
                    marginBottom: '15px',
                    border: '2px solid #e0e7ff',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)'
                  }}>
                    <p style={{ marginTop: 0, marginBottom: '14px', fontWeight: '700', color: '#4338ca', fontSize: '14px', textAlign: 'center' }}>Select Your OTP Method</p>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setPhoneAuthMethod('sms');
                          setShowPhoneMethodSelection(false);
                          setErrors(prev => ({ ...prev, phoneMethod: '' }));
                        }}
                        style={{
                          flex: 1,
                          padding: '13px 16px',
                          border: phoneAuthMethod === 'sms' ? '2px solid #0084ff' : '2px solid #ddd',
                          background: phoneAuthMethod === 'sms' ? 'linear-gradient(135deg, #0084ff 0%, #0066cc 100%)' : '#ffffff',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          color: phoneAuthMethod === 'sms' ? '#ffffff' : '#4b5563',
                          transition: 'all 0.3s ease',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          boxShadow: phoneAuthMethod === 'sms' ? '0 4px 12px rgba(0, 132, 255, 0.35)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        SMS
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPhoneAuthMethod('whatsapp');
                          setShowPhoneMethodSelection(false);
                          setErrors(prev => ({ ...prev, phoneMethod: '' }));
                        }}
                        style={{
                          flex: 1,
                          padding: '13px 16px',
                          border: phoneAuthMethod === 'whatsapp' ? '2px solid #25d366' : '2px solid #ddd',
                          background: phoneAuthMethod === 'whatsapp' ? 'linear-gradient(135deg, #25d366 0%, #20ba5f 100%)' : '#ffffff',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          color: phoneAuthMethod === 'whatsapp' ? '#ffffff' : '#4b5563',
                          transition: 'all 0.3s ease',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          boxShadow: phoneAuthMethod === 'whatsapp' ? '0 4px 12px rgba(37, 211, 102, 0.35)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        WhatsApp
                      </button>
                    </div>
                    {errors.phoneMethod && <span className="field-error" style={{ marginTop: '8px', display: 'block', fontSize: '12px' }}>{errors.phoneMethod}</span>}
                  </div>
                )}
                {showPhoneMethodSelection && <div style={{ height: '5px' }}></div>}

                <div className="modal-form-group">
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder=" "
                      value={formData.password}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="password">Password</label>
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <span className="field-error">{errors.password}</span>}
                </div>

                <div className="modal-form-group">
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder=" "
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
                </div>

                <div className="form-options">
                  <div className="admin-terms-container">
                    <input
                      type="checkbox"
                      id="agreeToTerms"
                      name="agreeToTerms"
                      className="auth-custom-checkbox"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="agreeToTerms" className="admin-terms-label">I agree to the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="link-btn">Terms of Service</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="link-btn">Privacy Policy</a></label>
                  </div>
                </div>

                {errors.agreeToTerms && <span className="field-error" style={{ display: 'block', marginTop: '-8px', marginBottom: '12px' }}>{errors.agreeToTerms}</span>}

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating Account...' : 'Create HQ Account'}
                </button>
              </form>

              <div className="form-footer">
                <p><span>Already have an HQ account?</span> <button type="button" onClick={onSwitchToLogin} className="link-btn">Sign In</button></p>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-image-section">
          <img src={signupImage} alt="Join Tecnvirons HQ" className="modal-image" />
          <div className="image-overlay">
            <div className="decorative-shape top-left-shape"></div>
            <div className="decorative-shape bottom-right-shape"></div>
            <div className="overlay-content">
              <div className="overlay-card">
                <h3>HQ Access</h3>
                <p>Manage clients, view platform analytics, and oversee operations.</p>
              </div>
              <div className="bottom-overlay">
                <h3>Platform Management,<br />Simplified.</h3>
                <p>Get started with the HQ Dashboard today!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSignupModal;
